import {
  Button as MUIButton,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../layouts/dashboard';
import { paths } from 'src/routes/paths';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import ErrorPage403 from 'src/pages/error/403';
import { deleteGroup, GroupItem, listGroups } from '../../services/groupService';

const PAGE_SIZE = 20;

// Every request of this screen lives here, under the PermissionGuard in the default export.
const GroupsTable = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<GroupItem | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(
    async (p: number) => {
      try {
        const res = await listGroups(p, PAGE_SIZE);
        setGroups(res.data);
        setTotal(res.total);
      } catch (error: any) {
        notify(error?.response?.data?.message || error?.message, { type: 'error' });
      }
    },
    [notify]
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  const confirmDelete = async () => {
    if (!target) return;
    setPending(true);
    try {
      await deleteGroup(target.id);
      setTarget(null);
      notify(translate('module.groups.deleteSuccess'), { type: 'success' });
      // The last row of a page may be gone: step back a page, otherwise reload this one.
      if (groups.length === 1 && page > 1) setPage(page - 1);
      else await load(page);
    } catch (error: any) {
      // A failed delete does not reload: the table keeps showing what it showed.
      setTarget(null);
      notify(error?.response?.data?.message || error?.message, { type: 'error' });
    } finally {
      setPending(false);
    }
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate('resource.groups')}
        links={[{ name: translate('resource.dashboard'), href: paths.dashboard.root }, { name: translate('resource.groups') }]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{translate('module.groups.name')}</TableCell>
                <TableCell>{translate('module.groups.memberCount')}</TableCell>
                <TableCell>{translate('module.groups.permissionCount')}</TableCell>
                <TableCell>{translate('module.groups.createdAt')}</TableCell>
                <TableCell>{translate('module.groups.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>{g.memberCount}</TableCell>
                  <TableCell>{g.permissionCount}</TableCell>
                  <TableCell>{g.createdAt ? new Date(g.createdAt).toLocaleString() : ''}</TableCell>
                  <TableCell>
                    <PermissionButton resource="groups" action="delete">
                      <MUIButton color="error" size="small" onClick={() => setTarget(g)}>
                        {translate('module.groups.delete')}
                      </MUIButton>
                    </PermissionButton>
                  </TableCell>
                </TableRow>
              ))}
              {groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>{translate('module.groups.empty')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, p) => setPage(p + 1)}
        />
      </Card>
      <Dialog open={!!target} onClose={() => !pending && setTarget(null)}>
        <DialogTitle>{translate('module.groups.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <strong>{target?.name}</strong>
            <div>{translate('module.groups.members', { count: target?.memberCount ?? 0 })}</div>
            <div>{translate('module.groups.permissions', { count: target?.permissionCount ?? 0 })}</div>
            <div>{translate('module.groups.deleteWarning')}</div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MUIButton disabled={pending} onClick={() => setTarget(null)}>
            {translate('module.groups.cancel')}
          </MUIButton>
          <MUIButton color="error" variant="contained" disabled={pending} onClick={confirmDelete}>
            {translate('module.groups.delete')}
          </MUIButton>
        </DialogActions>
      </Dialog>
    </DashboardContent>
  );
};

const GroupsList = () => (
  <PermissionGuard resource="groups" action="read" fallback={<ErrorPage403 />}>
    <GroupsTable />
  </PermissionGuard>
);

export default GroupsList;
